import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import { ProfileImage, SearchBar } from '../ui';
import { CHECKBOX_CLASS } from './CollapsibleCheckboxSection';
import {
  DEFAULT_EMPLOYEE_STATUS,
  getEmployeeStatusColorClass,
} from './employeeAvailabilityUtils';

const getEmployeeId = employee => employee?.user_id || employee?.employee_id;

const getSpecRole = employee =>
  (employee?.spec_role || employee?.role || employee?.role_name || '').trim();

/**
 * Searchable employee list with avatar cards, role filter, and multi-select checkboxes.
 */
const EmployeeCheckboxList = ({
  title = 'Employees',
  employees = [],
  selectedIds = [],
  onToggleEmployee,
  onSelectAll,
  isLoading = false,
  isValidatingAvailability = false,
  employeeStatuses = {},
  error = null,
  emptyMessage = 'No employees found',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(null);
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const roleFilterRef = useRef(null);

  const roleOptions = useMemo(() => {
    const roles = new Set();
    employees.forEach(employee => {
      const role = getSpecRole(employee);
      if (role) roles.add(role);
    });
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  useEffect(() => {
    if (!selectedRoleFilter) return;
    if (!roleOptions.includes(selectedRoleFilter)) {
      setSelectedRoleFilter(null);
    }
  }, [roleOptions, selectedRoleFilter]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (roleFilterRef.current && !roleFilterRef.current.contains(event.target)) {
        setIsRoleFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = useMemo(() => {
    let list = employees;

    if (selectedRoleFilter) {
      list = list.filter(employee => getSpecRole(employee) === selectedRoleFilter);
    }

    const query = searchTerm.trim().toLowerCase();
    if (!query) return list;

    return list.filter(employee => {
      const name = (employee.display_name || employee.user_name || employee.name || '').toLowerCase();
      const role = getSpecRole(employee).toLowerCase();
      const id = String(employee.employee_id || employee.user_id || '').toLowerCase();
      return name.includes(query) || role.includes(query) || id.includes(query);
    });
  }, [employees, searchTerm, selectedRoleFilter]);

  const allFilteredSelected =
    filteredEmployees.length > 0 &&
    filteredEmployees.every(employee => selectedIds.includes(getEmployeeId(employee)));

  const showAvailabilityStatus =
    isValidatingAvailability || Object.keys(employeeStatuses).length > 0;

  return (
    <div className="flex flex-col gap-3 w-full min-w-0">
      <div className="flex items-stretch gap-2 w-full">
        <div className="flex-1 min-w-0">
          <SearchBar
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target?.value ?? '')}
            iconType="search"
            className="w-full"
          />
        </div>

        <div className="relative flex-shrink-0" ref={roleFilterRef}>
          <button
            type="button"
            onClick={() => setIsRoleFilterOpen(open => !open)}
            className={`h-14 w-14 flex items-center justify-center rounded-[10px] border border-gray-300 bg-neutral-50/80 shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] backdrop-blur-[30px] transition-colors ${
              selectedRoleFilter
                ? 'text-[#1B6B52] border-[#6BABA0]/60 bg-[#E8F7F2]'
                : 'text-gray-600 hover:text-black hover:bg-white'
            }`}
            aria-label="Filter by role"
            title="Filter by role"
          >
            <Filter className="w-5 h-5" strokeWidth={2} />
          </button>

          {isRoleFilterOpen && (
            <div className="absolute top-full right-0 mt-1 py-1 min-w-[12rem] max-w-[16rem] bg-white rounded-xl shadow-lg border border-zinc-200 z-30 max-h-60 overflow-y-auto">
              <button
                type="button"
                onClick={() => {
                  setSelectedRoleFilter(null);
                  setIsRoleFilterOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm font-['Open_Sans'] hover:bg-gray-50 ${
                  !selectedRoleFilter ? 'text-[#1B6B52] font-medium bg-[#E8F7F2]/50' : 'text-black'
                }`}
              >
                All roles
              </button>
              {roleOptions.length === 0 ? (
                <p className="px-4 py-2 text-xs text-gray-500 font-['Open_Sans']">No roles available</p>
              ) : (
                roleOptions.map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => {
                      setSelectedRoleFilter(role);
                      setIsRoleFilterOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm font-['Open_Sans'] hover:bg-gray-50 truncate ${
                      selectedRoleFilter === role
                        ? 'text-[#1B6B52] font-medium bg-[#E8F7F2]/50'
                        : 'text-black'
                    }`}
                  >
                    {role}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {selectedRoleFilter && (
        <p className="text-xs text-gray-500 font-['Open_Sans']">
          Filtered by role: <span className="text-[#1B6B52] font-medium">{selectedRoleFilter}</span>
        </p>
      )}

      <div className="flex items-center justify-between w-full">
        <span className="text-base font-semibold text-black font-['Open_Sans']">{title}</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className={CHECKBOX_CLASS}
            checked={allFilteredSelected}
            disabled={filteredEmployees.length === 0 || isLoading}
            onChange={e => onSelectAll?.(e.target.checked, filteredEmployees)}
          />
          <span className="text-sm text-gray-500 font-['Open_Sans']">Select all</span>
        </label>
      </div>

      <div className="flex flex-col gap-2 max-h-[28rem] overflow-y-auto pr-1">
        {isLoading && (
          <p className="text-sm text-gray-500 font-['Open_Sans'] py-4 text-center">Loading employees...</p>
        )}
        {!isLoading && isValidatingAvailability && (
          <p className="text-sm text-gray-500 font-['Open_Sans'] py-2 text-center">
            Checking employee availability...
          </p>
        )}
        {error && !isLoading && (
          <p className="text-sm text-red-600 font-['Open_Sans'] py-2">{error}</p>
        )}
        {!isLoading && !error && filteredEmployees.length === 0 && (
          <p className="text-sm text-gray-500 font-['Open_Sans'] py-4 text-center">{emptyMessage}</p>
        )}
        {!isLoading &&
          !error &&
          filteredEmployees.map(employee => {
            const employeeId = getEmployeeId(employee);
            const isSelected = selectedIds.includes(employeeId);
            const displayName =
              employee.display_name || employee.user_name || employee.name || 'Unknown';
            const specRole = getSpecRole(employee);
            const employeeStatus = employeeStatuses[employeeId] || DEFAULT_EMPLOYEE_STATUS;

            return (
              <label
                key={employeeId}
                className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white border border-zinc-200 cursor-pointer hover:border-[#6BABA0]/50 transition-colors"
              >
                <ProfileImage
                  userId={employee.user_id}
                  userName={displayName}
                  imagePath={employee.image_path}
                  size="md"
                  showLoading={false}
                  lazy
                />
                <div className="flex-1 min-w-0">
                  <p className="text-base text-black font-['Open_Sans'] truncate">{displayName}</p>
                  {specRole && (
                    <p className="text-xs text-gray-500 font-['Open_Sans'] truncate">{specRole}</p>
                  )}
                  {showAvailabilityStatus && (
                    <p
                      className={`text-xs font-medium font-['Open_Sans'] truncate ${getEmployeeStatusColorClass(
                        employeeStatus.status
                      )}`}
                    >
                      {isValidatingAvailability ? 'Checking...' : employeeStatus.statusMessage}
                    </p>
                  )}
                </div>
                <input
                  type="checkbox"
                  className={`${CHECKBOX_CLASS} flex-shrink-0`}
                  checked={isSelected}
                  onChange={() => onToggleEmployee?.(employee)}
                />
              </label>
            );
          })}
      </div>
    </div>
  );
};

export default EmployeeCheckboxList;
